import React from 'react';
import { Link } from 'react-router-dom';
import Highlight from 'react-highlight';
import 'highlight.js/styles/stackoverflow-dark.css';

const HazardPointers: React.FC = () => {

    return (
        <div>
            <h1>Hazard Pointers</h1>
            <hr />

            <p>
                Recently I’ve been working on lock-free data structures in C++, specifically the <strong>Michael & Scott queue</strong>.
                While the core algorithm is fairly simple using atomic operations, the real difficulty appears after
                the structure is already correct.
            </p>

            <p>
                The actual problem is not synchronization — it is memory reclamation.
            </p>

            <p>
                <em><strong>When is it safe to delete a node that has been removed from a lock-free structure?</strong></em>
            </p>

            <hr />

            <p>
                <strong>
                    Note: This implementation is not production-ready code.
                    It is a simplified version I built while learning hazard pointers and exploring how memory
                    reclamation works in lock-free systems. The goal here is to share the journey and intuition,
                    not to provide a fully optimized or industrial-grade implementation.
                </strong>
            </p>

            <hr />

            <p>
                At first glance, it seems like the answer is “when it is no longer reachable”.
                But in concurrent systems, reachability is not equivalent to safety.
            </p>

            <p>
                A naive implementation looks correct but is fundamentally unsafe:
            </p>

            <div style={{ paddingLeft: '15%', paddingRight: '15%', textAlign: 'left' }}>
                <Highlight className='language-cpp'>
{`Node* old = head.load(std::memory_order_acquire);
head.compare_exchange_strong(old, old->next);
delete old; // ❌ unsafe in concurrent context`}
                </Highlight>
            </div>

            <p>
                Another thread may still hold a reference to <strong>old</strong> and dereference it after deletion.
            </p>

            <hr />

            <p>
                This is where <strong>Hazard Pointers</strong> come in.
            </p>

            <p>
                Instead of tracking ownership, threads explicitly declare:
            </p>

            <p>
                <em>“This is a pointer I might dereference — do not reclaim it.”</em>
            </p>

            <hr />

            <p>
                The system maintains a global hazard pointer table:
            </p>

            <div style={{ paddingLeft: '15%', paddingRight: '15%', textAlign: 'left' }}>
                <Highlight className='language-cpp'>
{`static constexpr size_t NUM_THREADS = 8;
static constexpr size_t HP_PER_THREAD = 8;
static constexpr size_t RETIRED_THRESHOLD = 32;

std::atomic<void*> hazard_pointers[NUM_THREADS * HP_PER_THREAD];`}
                </Highlight>
            </div>

            <hr />

            <p>
                Each thread uses a RAII guard to manage hazard pointer slots.
                The guard publishes a pointer, ensures correctness via recheck, and clears the slot automatically.
            </p>

            <div style={{ paddingLeft: '15%', paddingRight: '15%', textAlign: 'left' }}>
                <Highlight className='language-cpp'>
{`class hazard_pointer_guard
{
public:
    hazard_pointer_guard()
    {
        assert(hp_slot_index < HP_PER_THREAD);

        hp_index = thread_index * HP_PER_THREAD + hp_slot_index;
        ++hp_slot_index;
    }

    ~hazard_pointer_guard()
    {
        hazard_pointers[hp_index].store(nullptr, std::memory_order_release);
    }

    template <typename T>
    T* protect(const std::atomic<T*>& src)
    {
        T* ptr;

        do
        {
            ptr = src.load(std::memory_order_acquire);

            hazard_pointers[hp_index].store(
                ptr,
                std::memory_order_release
            );
        }
        while (ptr != src.load(std::memory_order_acquire));

        return ptr;
    }

private:
    size_t hp_index;
};`}
                </Highlight>
            </div>

            <p>
                The critical pattern remains:
                <strong> load → publish → recheck </strong>.
            </p>

            <hr />

            <p>
                When nodes are removed from the structure, they are not immediately freed.
                Instead, they are placed into a thread-local retired list.
            </p>

            <div style={{ paddingLeft: '15%', paddingRight: '15%', textAlign: 'left' }}>
                <Highlight className='language-cpp'>
{`struct retired_node
{
    void* ptr;
    void (*deleter)(void*);
};

thread_local std::vector<retired_node> retired;

template <typename T>
void retire(T* ptr)
{
    retired.push_back(
        retired_node{
            ptr,
            [](void* p)
            {
                delete static_cast<T*>(p);
            }
        }
    );

    if (retired.size() >= RETIRED_THRESHOLD)
        scan();
}`}
                </Highlight>
            </div>

            <p>
                The <strong>RETIRED_THRESHOLD</strong> batches reclamation work instead of scanning on every operation.
            </p>

            <hr />

            <p>
                Periodically, the system scans all hazard pointers and reclaims memory that is no longer protected.
            </p>

            <div style={{ paddingLeft: '15%', paddingRight: '15%', textAlign: 'left' }}>
                <Highlight className='language-cpp'>
{`void scan()
{
    std::vector<void*> hp;
    hp.reserve(NUM_THREADS * HP_PER_THREAD);

    for (size_t i = 0; i < NUM_THREADS * HP_PER_THREAD; ++i)
    {
        void* const ptr = hazard_pointers[i].load(std::memory_order_acquire);
        if (ptr)
            hp.push_back(ptr);
    }

    auto it = retired.begin();
    while (it != retired.end())
    {
        if (std::find(hp.begin(), hp.end(), it->ptr) == hp.end())
        {
            it->deleter(it->ptr);
            it = retired.erase(it);
        }
        else
        {
            ++it;
        }
    }
}`}
                </Highlight>
            </div>

            <hr />

            <p>
                What became clear during implementation is that hazard pointers are not a data structure,
                but a <strong>protocol built on top of the memory model</strong>.
            </p>

            <p>
                Small mistakes — especially in indexing, slot management, or ordering — immediately lead to
                use-after-free bugs that are extremely hard to reproduce due to timing dependence.
            </p>

            <hr />

            <p>
                The key insight for me was:
            </p>

            <p>
                <em>
                    Hazard pointers do not represent ownership. They represent temporary reachability.
                </em>
            </p>

            <p>
                The question shifts from:
                <strong> “Who owns this memory?” </strong>
                to:
                <strong> “Could any thread still reach this pointer right now?” </strong>
            </p>

            <hr />

            <p>
                If you want to go further, the next natural step is
                <strong> epoch-based reclamation (EBR)</strong>,
                which removes per-pointer tracking in favor of time-based reclamation.
            </p>

            <p>
                You could check out this code snippet over this <Link to="https://godbolt.org/z/qKM375n1E"><strong>godbolt link</strong></Link><br />
                Feel free to ask me any questions you might have!
            </p>

        </div>
    );
};

export default HazardPointers;